import { ForbiddenComponent } from './exceptions/forbidden/forbidden.component';
import { ModuleWithProviders } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CanDeactivateGuard } from './shared/components/can-deactivate/can-deactivate.guard';
import { EnvironmentComponent } from './environment/environment.component';
import { routesTracing } from './trace-management/trace-management.routes';
import { EventLogComponent } from './event-log/event.log.component';
import { CanActivateViaAuthGuard } from './bz-authentication/bz-can-activate-auth';
import { DeploymentComponent } from './deployment/deployment.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { BpmConsumeComponent } from './bpm-consume/bpm-consume.component';
import { RoutesSecurity } from './security/security.routes';
import { SyncDataComponent } from './sync-data/sync-data.component';
import { SlideMainNavigationComponent } from './slide-navigation/main/slide-main-navigation.component';
import { SlideSettingsNavigationComponent } from './slide-navigation/settings/slide-settings-navigation.component';
import { ChangePasswordComponent } from './auth/auto-login/change-password/change-password.component';
import { ManagementConsoleComponent } from './management-console/management-console.component';
import { AdminLoginComponent } from './auth/auto-login/admin-login/admin-login.component';
import { EmailSentComponent } from './auth/auto-login/email-sent/email-sent.component';
import { ChangeAdministratorComponent } from './change-administrator/change-administrator.component';
import { AccountInformationComponent } from './account-information/account-information.component';
import { SendAdminEmailComponent } from './auth/auto-login/send-admin-email/send-admin-email.component';
import { SendPasswordEmailComponent } from './auth/auto-login/send-password-email/send-password-email.component';
import { CanActivateAutologinGuard } from './bz-authentication/bz-can-activate-autologin';
import { CanActivateInSessionGuard } from './bz-authentication/bz-can-activate-in-session';
import { RoutesConnect } from './connect/connect.routes';
import { RoutesScheduler } from './scheduler/scheduler.routes';
import { DeleteCasesComponent } from './delete-cases/delete-cases.component';
import { RoutesOrganization } from './organization/organization.routes';
import { ChangePasswordAdminComponent } from './change-password-admin/change-password-admin.component';
import { RefreshComponent } from './shared/components/refresh/refresh.component';
import { RoutesRPA } from './rpa-bots/rpa-bots-routing.module';
import { menus } from './core/models/constants';
import { OutOfServiceComponent } from './auth/out-of-service/out-of-service.component';
import { CanActivateEmptyWorkspaceGuard } from './bz-authentication/bz-can-activate-empty-workspace';
import { MigrateStakeholdersComponent } from './migrate-stakeholders/migrate-stakeholders.component';
import { GlobalProxyComponent } from './global-proxy/global-proxy.component';
import { OauthCallbackComponent } from './connect/auth-code-grant/oauth-callback/oauth-callback.component';
import { RoutesDataVisualization } from './datavisualization/datavisualization.routes';
import { SequencesComponent } from './sequences/sequences.component';
import { SyncFilesComponent } from './sync-files/sync-files.component';
import { ImportSyncFilesComponent } from './sync-files/import-sync-files/import-sync-files.component';
import { TranslateModule } from '@ngx-translate/core';
import { TraceManagementComponent } from './trace-management/trace-management.component';
import { InitialPageComponent } from './initial-page/initial-page.component';
import { AuditLogComponent } from './auditlog/auditlog.component';
import { CustomCertificateComponent } from './custom-certificate/custom-certificate.component';
import { BaseKnowledgeCatalogComponent } from './base-knowledge-catalog/base-knowledge-catalog.component';

// Route Configuration
const routes: Routes = [
  {
    path: 'redirect',
    component: OauthCallbackComponent,
    data: {
      ignoreAuth: 'true'
    },
    pathMatch: 'full'
  },
  {
    path: 'out-of-service',
    component: OutOfServiceComponent,
    data: {
      ignoreAuth: 'true'
    },
    pathMatch: 'full'
  },
  {
    path: 'change-password',
    component: ChangePasswordComponent,
    canActivate: [CanActivateAutologinGuard],
    data: {
      ignoreAuth: 'true'
    },
    pathMatch: 'full'
  },
  {
    path: 'email-sent',
    component: EmailSentComponent,
    canActivate: [CanActivateAutologinGuard],
    data: {
      ignoreAuth: 'true'
    },
    pathMatch: 'full'
  },
  {
    path: 'send-password-email',
    component: SendPasswordEmailComponent,
    canActivate: [CanActivateAutologinGuard],
    data: {
      ignoreAuth: 'true'
    },
    pathMatch: 'full'
  },
  {
    path: 'send-admin-email',
    component: SendAdminEmailComponent,
    canActivate: [CanActivateAutologinGuard],
    data: {
      ignoreAuth: 'true'
    },
    pathMatch: 'full'
  },
  {
    path: 'login',
    component: AdminLoginComponent,
    canActivate: [CanActivateEmptyWorkspaceGuard, CanActivateAutologinGuard, CanActivateInSessionGuard],
    pathMatch: 'full'
  },
  {
    path: '',
    redirectTo: '/mc/initial-page',
    pathMatch: 'full'
  },
  {
    path: 'mc',
    component: ManagementConsoleComponent,
    canActivate: [CanActivateEmptyWorkspaceGuard, CanActivateViaAuthGuard],
    children: [
      {
        path: '',
        component: SlideMainNavigationComponent,
        outlet: 'sidebar-panel'
      },
      {
        path: 'initial-page',
        component: InitialPageComponent,
        pathMatch: 'full'
      },
      {
        path: '',
        redirectTo: '/mc/initial-page',
        pathMatch: 'full'
      },
      {
        path: 'Refresh',
        component: RefreshComponent,
        data: { title: '' }
      },
      {
        path: 'Forbidden',
        component: ForbiddenComponent,
        data: { title: '' }
      },
      {
        path: menus.Environment.path,
        component: EnvironmentComponent,
        data: { title: menus.Environment.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.GlobalProxy.path,
        component: GlobalProxyComponent,
        data: { title: menus.GlobalProxy.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.EventTracing.path,
        component: EventLogComponent,
        data: { title: menus.EventTracing.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.AuditLog.path,
        component: AuditLogComponent,
        data: { title: menus.AuditLog.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.DeploymentPackage.path,
        component: DeploymentComponent,
        data: { title: menus.DeploymentPackage.title },
        canActivate: [CanActivateViaAuthGuard],
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: menus.SyncData.path,
        component: SyncDataComponent,
        data: { title: menus.SyncData.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.SyncFilesExport.path,
        component: SyncFilesComponent,
        data: { title: menus.SyncFilesExport.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.SyncFilesImport.path,
        component: ImportSyncFilesComponent,
        data: { title: menus.SyncFilesImport.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.MigrateStakeholders.path,
        component: MigrateStakeholdersComponent,
        data: { title: menus.MigrateStakeholders.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.MaintenanceWindow.path,
        component: MaintenanceComponent,
        data: { title: menus.MaintenanceWindow.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.Sequences.path,
        component: SequencesComponent,
        data: { title: menus.Sequences.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.CustomCertificate.path,
        component: CustomCertificateComponent,
        data: { title: menus.CustomCertificate.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.BaseKnowledgeCatalog.path,
        component: BaseKnowledgeCatalogComponent,
        data: { title: menus.BaseKnowledgeCatalog.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.TracingManagement.path,
        component: TraceManagementComponent,
        data: { title: menus.TracingManagement.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.BPUMonitoring.path,
        component: BpmConsumeComponent,
        data: { title: menus.BPUMonitoring.title },
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.DeleteCases.path,
        data: { title: menus.DeleteCases.title },
        component: DeleteCasesComponent,
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: menus.ChangeWebAdminPassword.path,
        data: { title: menus.ChangeWebAdminPassword.title },
        component: ChangePasswordAdminComponent,
        canActivate: [CanActivateViaAuthGuard]
      },
      ...routesTracing,
      ...RoutesSecurity,
      ...RoutesOrganization,
      ...RoutesConnect,
      ...RoutesScheduler,
      ...RoutesRPA,
      ...RoutesDataVisualization,
      {
        path: '*',
        redirectTo: 'Environment',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'settings',
    component: ManagementConsoleComponent,
    children: [
      {
        path: '',
        component: SlideSettingsNavigationComponent,
        outlet: 'sidebar-panel'
      },
      {
        path: '',
        redirectTo: '/settings/account-information',
        pathMatch: 'full'
      },
      {
        path: 'Refresh',
        component: RefreshComponent,
        data: { title: '' }
      },
      {
        path: 'account-information',
        data: { title: 'MENU.ACCOUNTINFORMATION' },
        component: AccountInformationComponent,
        canActivate: [CanActivateViaAuthGuard]
      },
      {
        path: 'change-administrator',
        data: { title: 'MENU.CHANGEAUTOLOGINADMINISTRATOR' },
        component: ChangeAdministratorComponent,
        canActivate: [CanActivateViaAuthGuard]
      }
    ]
  },
  {
    path: '**',
    redirectTo: '/mc/Environment',
    pathMatch: 'full'
  },
];

export const routing: ModuleWithProviders<TranslateModule> = RouterModule.forRoot(routes);
